import React, { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { Button, Drawer, Stack } from "@mui/material";
import { AsyncContentMui, SimpleHeadMui } from "components";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useActionHook, useDidUpdate } from "hooks";
import {
  IApiServiceError,
  useDealActions,
  useDealIdSelector,
  useInvestorsSelector,
  useMeasureParametersSelector,
  useReportsMetaSelector
} from "store";
import { IUpdateMeasureDataPayload, MonitoringDataFiles } from "store/types";
import apiErrorsToFormik from "utils/api-errors-to-formik";
import toIsoMidday from "utils/to-iso-midday";

import { Errors } from "../Groups-form/GroupsFormContext";
import { FieldsState, SaveOneForm } from "../Save-one-form/SaveOneForm";

const initialFields: FieldsState = {
  deal_id: null,
  measure_id: null,
  investor_id: null,
  bank_detail_id: null,
  round_id: null,
  date: null,
  accounting_date: null,
  currency: null,
  value: "",
  description: null,
  effective_range_from: null,
  effective_range_to: null,
  files: null,
  is_child: false
};

interface Props {
  id: number | null;
  close: () => void;
}

export const UpdateOne: React.FC<Props> = ({ id, close }) => {
  const { getRoundsAction } = useDealActions();
  const [loading, setLoading] = useState<boolean>(false);
  const { deals } = useReportsMetaSelector();
  const measures = useMeasureParametersSelector();
  const investors = useInvestorsSelector();
  const isDealContext = useDealIdSelector();
  // const formatDate = useFormatDateHook();
  // const queries = useReportsQueriesSelector();
  const { updateMeasureDataAction, getOneMeasureDataAction, getDealRoundsWithInvestorsAndStoreAction } =
    useDealActions();
  const [filesErrors, setFilesErrors] = useState<string[] | null>(null);
  const { action: getDealRounds, data: dealRoundsData } = useActionHook(getRoundsAction);

  const {
    action: getOne,
    data: monitoringData,
    resetData: resetMonitoringData
  } = useActionHook(getOneMeasureDataAction, {
    onSuccess: (data) => {
      if (data.deal_id) {
        getDealRounds({ dealId: data.deal_id, dispatch: false });
      }
    }
  });

  useDidUpdate(() => {
    if (id) {
      getOne(id);
    }
  }, [id]);

  const initialValues: FieldsState = useMemo(() => {
    const init: FieldsState = { ...initialFields };

    if (!monitoringData) return init;
    const {
      deal_id,
      measure_id,
      investor_id,
      date,
      accounting_date,
      currency,
      value,
      description,
      is_negative,
      round_id,
      effective_range_from,
      effective_range_to,
      investor_files,
      is_child
    } = monitoringData;

    init.deal_id = { id: deal_id, title: "None" };
    init.is_child = is_child;
    init.measure_id = measures.find((item) => item.id === measure_id) || null;
    init.investor_id = investors.find((item) => item.id === investor_id) || null;

    if (date) {
      init.date = dayjs(date).toISOString();
    }

    if (accounting_date) {
      init.accounting_date = dayjs(accounting_date).toISOString();
    }

    if (effective_range_from) {
      init.effective_range_from = dayjs(effective_range_from).toISOString();
    }

    if (effective_range_to) {
      init.effective_range_to = dayjs(effective_range_to).toISOString();
    }
    if (investor_files) {
      init.files = {
        dbFiles: investor_files.reduce(
          (acc, file) => {
            acc[crypto.randomUUID()] = file;
            return acc;
          },
          {} as Record<string, MonitoringDataFiles>
        ),
        localFiles: null
      };
    }

    if (round_id) {
      init.round_id = round_id;
    }

    init.currency = currency || null;
    init.value = is_negative ? String(Number(value) * -1) : value;
    init.description = description || null;

    return init;
  }, [monitoringData, deals, measures, investors]);

  const { values, setFieldValue, resetForm, setErrors, errors } = useFormik({
    initialValues: initialValues,
    onSubmit: () => {},
    validateOnChange: false,
    enableReinitialize: true
  });

  const isSubmitDisabled = useMemo(() => {
    return !!Object.keys(errors).length;
  }, [errors]);

  // const { setQueriesAction } = useReportsActions();

  const closeAndReset = useCallback(() => {
    close();
    resetForm();
    resetMonitoringData();
    setFilesErrors(null);
  }, []);

  const onSubmit = useCallback(async () => {
    try {
      setLoading(true);
      setFilesErrors(null);
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        deal_id,

        measure_id,
        investor_id,
        accounting_date,
        date,
        effective_range_from,
        effective_range_to,
        ...rest
      } = values;

      if (!id) throw new Error(Errors.SOMETHING_WENT_WRONG);
      const body: IUpdateMeasureDataPayload = { id };

      if (investor_id) {
        body.investor_id = investor_id.id;
      }

      if (date) {
        body.date = toIsoMidday(date); // in case if user not edited field effective date we need to bring it to midday, because server provided it with 00:00 time
        // body.date = formatDate({ date: date.toString(), format: "YYYY-MM-DD" });
      }
      //added ability for user to reset accounting date
      // if (accounting_date) {
      body.accounting_date = accounting_date;
      // body.accounting_date = formatDate({ date: accounting_date.toString(), format: "YYYY-MM-DD" });
      // }

      //added ability for user to reset effective date range
      body.effective_range_from = effective_range_from;
      body.effective_range_to = effective_range_to;

      if (!rest.description) {
        body.description = null;
      }

      if (measure_id?.id) {
        body.measure_id = measure_id?.id;
      }

      // Typescript issues, realy hate Object.keys or "for of" with TS
      Object.keys(rest).forEach((key) => {
        if ((rest as any)[key]) {
          (body as any)[key] = (rest as any)[key];
        }
      });

      await updateMeasureDataAction(body);
      isDealContext && (await getDealRoundsWithInvestorsAndStoreAction(isDealContext));
      // setQueriesAction(queries);
      toast("Data Updated");
      closeAndReset();
    } catch (error) {
      const apiErrors = (error as IApiServiceError).errors;
      if (!apiErrors) return;
      const newFilesMessages = Object.keys(apiErrors)
        .filter((key) => key.startsWith("new_files."))
        .flatMap((key) => apiErrors[key]);
      if (newFilesMessages.length) {
        setFilesErrors(Array.from(new Set(newFilesMessages)));
      }
      setErrors(apiErrorsToFormik(initialFields, apiErrors));
    } finally {
      setLoading(false);
    }
  }, [values, id]);

  return (
    <Drawer
      sx={{
        ".MuiPaper-root": {
          borderTopRightRadius: "25px",
          borderBottoRightRadius: "25px"
        }
      }}
      open={!!id}
      onClose={close}
    >
      <Stack
        sx={{
          alignItems: "stretch",
          width: 500,
          bgcolor: "white",
          height: "100%",
          flexGrow: 1
        }}
      >
        <SimpleHeadMui onIconClick={closeAndReset}>Edit Value - {monitoringData?.deal_title}</SimpleHeadMui>
        <AsyncContentMui isLoading={loading} spinnerProps={{ size: 120 }}>
          <Stack
            sx={{
              gap: "20px",
              flexGrow: 1,
              padding: "20px",
              height: "100%",
              overflow: "auto"
            }}
          >
            <SaveOneForm
              setFieldValue={setFieldValue}
              values={values}
              errors={errors}
              setErrors={setErrors}
              rounds={dealRoundsData || []}
              isRoundsDisabled={!dealRoundsData?.length}
              filesErrors={filesErrors}
            />
          </Stack>
        </AsyncContentMui>
        <Stack direction="row" sx={{ justifyContent: "flex-end", border: "1px solid #dee2e6", p: "20px", gap: "10px" }}>
          <Button variant="outlined" onClick={closeAndReset}>
            Cancel
          </Button>
          <Button disabled={isSubmitDisabled} onClick={onSubmit} type="submit" variant="contained">
            Save
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};
